import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Cpu, Wrench, Rocket, ChevronRight, SkipForward } from 'lucide-react'
import MachineProfileEditor from '../mission-control/MachineProfileEditor'
import ToolLibraryEditor from '../mission-control/ToolLibraryEditor'
import { defaultProfile } from '../../hooks/useMachineProfiles'
import type { MachineProfile, Tool } from '../../types'

interface OnboardingFlowProps {
  onComplete: (machine: MachineProfile, tools: Tool[]) => void
}

const STEPS = ['welcome', 'machine', 'tools', 'ready'] as const
type Step = typeof STEPS[number]

export default function OnboardingFlow({ onComplete }: OnboardingFlowProps) {
  const [step, setStep] = useState<Step>('welcome')
  const [machine, setMachine] = useState<MachineProfile>(defaultProfile())
  const [tools, setTools] = useState<Tool[]>([])

  const stepIndex = STEPS.indexOf(step)

  const handleMachineSave = useCallback(() => {
    if (!machine.name.trim()) return
    setStep('tools')
  }, [machine])

  const handleFinish = useCallback(() => {
    onComplete(machine, tools)
  }, [machine, tools, onComplete])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#030712]">
      {/* Background glow */}
      <div
        className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.08) 0%, transparent 70%)' }}
      />

      <div className="relative z-10 w-full max-w-lg px-6">
        {/* Step dots */}
        <div className="flex justify-center gap-2 mb-8">
          {STEPS.map((s, i) => (
            <div
              key={s}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                i === stepIndex ? 'bg-cyan-400 scale-125' :
                i < stepIndex ? 'bg-cyan-400/40' : 'bg-slate-700'
              }`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {step === 'welcome' && (
            <StepCard key="welcome">
              <div className="text-center space-y-6">
                <div className="flex justify-center">
                  <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                    <Cpu className="w-8 h-8 text-cyan-400" />
                  </div>
                </div>
                <div>
                  <h1 className="font-mono text-xl text-slate-100 mb-2">Welcome to GCodr</h1>
                  <p className="font-mono text-sm text-slate-500 leading-relaxed">
                    AI-powered G-code generation for CNC machining.<br />
                    Let's set up your machine to get started.
                  </p>
                </div>
                <button
                  onClick={() => setStep('machine')}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg font-mono text-sm bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/25 transition-all"
                >
                  Get Started <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </StepCard>
          )}

          {step === 'machine' && (
            <StepCard key="machine">
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                    <Cpu className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h2 className="font-mono text-sm text-slate-200">Configure Your Machine</h2>
                    <p className="font-mono text-[10px] text-slate-500">Enter your CNC machine specs</p>
                  </div>
                </div>
                <div className="max-h-[50vh] overflow-y-auto pr-1">
                  <MachineProfileEditor
                    value={machine}
                    onChange={setMachine}
                    onSave={handleMachineSave}
                    onCancel={() => setStep('welcome')}
                    existingNames={[]}
                  />
                </div>
              </div>
            </StepCard>
          )}

          {step === 'tools' && (
            <StepCard key="tools">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                      <Wrench className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h2 className="font-mono text-sm text-slate-200">Tool Library</h2>
                      <p className="font-mono text-[10px] text-slate-500">Add your tools or skip for now</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setStep('ready')}
                    className="flex items-center gap-1 font-mono text-[10px] text-slate-500 hover:text-cyan-400 transition-colors"
                  >
                    <SkipForward className="w-3 h-3" /> Skip
                  </button>
                </div>
                <div className="max-h-[50vh] overflow-y-auto pr-1">
                  <ToolLibraryEditor
                    tools={tools}
                    operationType={machine.type === 'turning' ? 'turning' : 'milling'}
                    onChange={setTools}
                  />
                </div>
                <button
                  onClick={() => setStep('ready')}
                  className="w-full py-2 rounded-lg font-mono text-xs bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/25 transition-all"
                >
                  Continue
                </button>
              </div>
            </StepCard>
          )}

          {step === 'ready' && (
            <StepCard key="ready">
              <div className="text-center space-y-6">
                <div className="flex justify-center">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <Rocket className="w-8 h-8 text-emerald-400" />
                  </div>
                </div>
                <div>
                  <h2 className="font-mono text-xl text-slate-100 mb-2">You're Ready</h2>
                  <p className="font-mono text-sm text-slate-500 leading-relaxed">
                    Your machine is configured. Upload a drawing<br />
                    and let Trinity generate your G-code.
                  </p>
                </div>
                <button
                  onClick={handleFinish}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg font-mono text-sm bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 transition-all"
                >
                  Create Your First Job <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </StepCard>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

function StepCard({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="rounded-2xl border border-cyan-500/15 bg-[#0a1628]/80 backdrop-blur-sm p-6"
    >
      {children}
    </motion.div>
  )
}
